import { Select, Spin, Typography } from 'antd';
import { useTribe } from 'hooks/useTribe';
import _debounce from 'lodash/debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ClientsService } from 'services/ClientsService';
import { Center } from 'ui/Center';

const PER_PAGE = 10;
const SEARCH_DELAY = 800;
const selectStyles = { minWidth: '250px', maxWidth: '400px' };
const selectDropdownWidth = 400;

function ClientsSelect({
  onSelect,
  onClear,
  onClientsChange,
  onSelectedClientDataChange,
  pageTitle = '',
  clientId = null,
}) {
  const {
    user: { groups_ids },
  } = useSelector((state) => state.auth);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clients, setClients] = useState([]);
  const tribe_id = useTribe();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(null);
  const [totalClients, setTotalClients] = useState(0);
  const selectRef = useRef(null);

  useEffect(() => {
    if (clientId) {
      setSelectedClientId(+clientId);
      fetchClients(clientId);
    }
  }, []);

  useEffect(() => {
    if (page) {
      fetchClients();
    }
  }, [page]);

  useEffect(() => {
    if (onClientsChange) {
      onClientsChange(clients);
    }
  }, [clients]);

  const selectedClientData = useMemo(() => {
    if (selectedClientId) {
      return clients.find((client) => client.id === selectedClientId);
    }
    return null;
  }, [selectedClientId, clients]);

  useEffect(() => {
    if (selectedClientData) {
      const { full_name = '' } = selectedClientData;
      document.title = `${pageTitle} | ${full_name}`;
    } else {
      document.title = pageTitle;
    }

    if (onSelectedClientDataChange) {
      onSelectedClientDataChange(selectedClientData);
    }
  }, [selectedClientData]);

  const canFetchMore = useMemo(() => {
    return clients.length < totalClients && search;
  }, [clients, totalClients, search]);

  const fetchClients = async (defaultClientId) => {
    setLoading(true);

    try {
      const { items, total } = await ClientsService.getClientsSelect(
        page,
        PER_PAGE,
        search,
        defaultClientId,
        groups_ids,
        !tribe_id || tribe_id === 3 ? 0 : tribe_id
      );

      if (page > 1) {
        setClients([...clients, ...items]);
      } else {
        setClients(items);
      }

      setTotalClients(total);
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    selectRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (val) => {
    setSearch(val);

    if (val) {
      scrollToTop();
      setPage(null);
      setPage(1);
    }
  };

  // Handler for client selection
  const handleClientSelect = (id) => {
    setSelectedClientId(id);
    onSelect(id);
    localStorage.setItem('last_client_id', id);
  };

  // Clear all state
  const handleClear = () => {
    setSearch('');
    setSelectedClientId(null);
    setClients([]);
    setPage(null);
    setTotalClients(0);
    localStorage.removeItem('last_client_id');

    if (onClear) {
      onClear();
    }
  };

  // Handler for paginated scroll
  const handleScroll = (event) => {
    const { target } = event;
    const { scrollTop, offsetHeight, scrollHeight } = target;
    const isEnd = scrollTop + offsetHeight === scrollHeight;

    if (!loading && isEnd && canFetchMore) {
      setPage(page + 1);
    }
  };

  const debouncedSearchFn = useCallback(
    _debounce(handleSearch, SEARCH_DELAY),
    []
  );

  const renderDropdown = (menu) => {
    return (
      <>
        {menu}
        {loading && (
          <Center>
            <Spin spinning={loading} />
          </Center>
        )}
      </>
    );
  };

  const renderClientData = () => {
    if (!selectedClientData) {
      return null;
    }

    const { bin_iin, nds, leader } = selectedClientData;
    const ndsText = nds ? `НДС ${nds}%` : null;
    const content = [bin_iin, ndsText, leader].filter(Boolean).join(' - ');

    return (
      <Typography.Text
        type="secondary"
        style={{ fontSize: '14px', paddingLeft: '2px' }}
      >
        {content}
      </Typography.Text>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Select
        ref={selectRef}
        showSearch
        allowClear
        filterOption={false}
        style={selectStyles}
        popupMatchSelectWidth={selectDropdownWidth}
        dropdownRender={renderDropdown}
        placeholder="Поиск по клиенту"
        value={selectedClientId}
        onSelect={handleClientSelect}
        onSearch={debouncedSearchFn}
        onPopupScroll={handleScroll}
        onClear={handleClear}
        optionLabelProp="label"
      >
        {clients.map(({ id, full_name, bin_iin, leader }) => (
          <Select.Option key={id} value={id} label={full_name}>
            <div>{full_name}</div>
            <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
              {bin_iin} - {leader}
            </Typography.Text>
          </Select.Option>
        ))}
      </Select>

      {renderClientData()}
    </div>
  );
}

export default React.memo(ClientsSelect);
